// Decyzon — single source of truth for the result page render state.
// Pure function. No side effects. Used by PredictionResultPage to decide which
// blocks render (live trading surface, stale snapshot, broken route).
//
// Top-level state (kept stable for downstream consumers):
//   live_tradable   — verified live contract + live price + URL → full execution surface
//   stale_snapshot  — live source but live price connection lost or aging out → no exec UI
//   analysis_only   — DEPRECATED, retained in type for compat — never returned
//   broken_route    — verdict suppressed
//
// REMASTER: when state === "broken_route" we ALSO return `brokenKind` so the
// banner can render the right diagnosis instead of one vague message.

import type { PredictionResult } from "./predictionTypes";

export type ResultState =
  | "live_tradable"
  | "stale_snapshot"
  | "analysis_only"
  | "broken_route";

/** Why a route was marked broken — drives BrokenRouteBanner copy. */
export type BrokenKind =
  | "unverified"     // parser/source returned nothing for this cid
  | "region"         // platform doesn't serve this region
  | "closed"         // contract has resolved (end_date past)
  | "no_id"          // we never had a market_id to look up
  | "unknown";

export interface ResultStateInput {
  result: PredictionResult;
  livePrice: number | null;
  liveFetchedAt: number | null;
  liveSource?: "feed" | "predict_response" | "none";
}

export interface DerivedState {
  state: ResultState;
  brokenKind: BrokenKind | null;
}

const STALE_AFTER_MS = 30 * 60 * 1000; // 30 min

/** Backward-compat: returns just the state. New code should call
 *  `deriveResultStateFull` to also receive the broken_kind diagnosis. */
export function deriveResultState(input: ResultStateInput): ResultState {
  return deriveResultStateFull(input).state;
}

export function deriveResultStateFull({
  result,
  livePrice,
  liveFetchedAt,
  liveSource = "none",
}: ResultStateInput): DerivedState {
  const scannerTruth = Boolean((result as any)?.scanner_truth);
  const scannerScannedAt = (result as any)?.scanned_at ?? null;
  // 1. Closed contract → broken_route(closed). Verdict has no live meaning.
  const endDate = (result as any)?.end_date ?? null;
  if (endDate) {
    const ms = Date.parse(endDate);
    if (Number.isFinite(ms) && ms < Date.now()) {
      return { state: "broken_route", brokenKind: "closed" };
    }
  }

  // 2. No URL → either no_id (we never had it) or unverified.
  const noUrl = !result.market_url || result.market_url.length < 8;
  const noId = !result.market_id;
  if (noUrl && noId) return { state: "broken_route", brokenKind: "no_id" };
  if (noUrl) return { state: "broken_route", brokenKind: "unverified" };

  // 3. Availability flag from upstream. region_block is its own diagnosis;
  //    not_listed = "we couldn't verify the contract" → unverified.
  const avail = result.availability?.status;
  if (avail === "regional_block") return { state: "broken_route", brokenKind: "region" };
  if (avail === "not_listed") return { state: "broken_route", brokenKind: "unverified" };

  // 4. Source status from predict(). Only `unverified` triggers broken — every
  //    other partial state should drop into stale_snapshot, NOT broken_route.
  //    This is a remaster fix: previously `partial` and `analysis_only` from
  //    Kalshi (zero-bid markets) collapsed into broken_route, which surprised
  //    users on legitimate live contracts.
  const ss = (result as any)?.source_status as string | undefined;
  if (ss === "unverified" || ss === "unavailable") {
    return { state: "broken_route", brokenKind: "unverified" };
  }

  // 4b. Feed-origin scanner truth counts as live if we already have a recent
  // verified scanner row, even before useLivePrice finishes its first fetch.
  if (scannerTruth && result.market_probability != null && scannerScannedAt) {
    const scannedMs = Date.parse(scannerScannedAt);
    if (Number.isFinite(scannedMs) && Date.now() - scannedMs <= STALE_AFTER_MS) {
      return { state: "live_tradable", brokenKind: null };
    }
  }

  // 5. Live-tradable requires a real live price connection from market_feed.
  const haveLive = livePrice != null && liveSource === "feed";
  if (haveLive) {
    if (liveFetchedAt != null && Date.now() - liveFetchedAt > STALE_AFTER_MS) {
      return { state: "stale_snapshot", brokenKind: null };
    }
    return { state: "live_tradable", brokenKind: null };
  }

  // 6. Default: snapshot.
  return { state: "stale_snapshot", brokenKind: null };
}

export function stateLabel(s: ResultState): string {
  switch (s) {
    case "live_tradable": return "LIVE";
    case "stale_snapshot": return "SNAPSHOT";
    case "analysis_only": return "ANALYSIS ONLY";
    case "broken_route": return "ROUTE UNVERIFIED";
  }
}

export function stateAccent(s: ResultState): string {
  switch (s) {
    case "live_tradable": return "text-accent border-accent/40 bg-accent/10";
    case "stale_snapshot": return "text-foreground border-border bg-muted/40";
    case "analysis_only": return "text-muted-foreground border-border bg-card";
    case "broken_route": return "text-destructive border-destructive/40 bg-destructive/10";
  }
}

// Feed-side predictor: same contract that the scanner just wrote.
// Cards that wouldn't render as live_tradable on the result page must NOT
// promise BUY in the feed. Used to downgrade verb + badge.
export interface FeedRowLike {
  market_url?: string | null;
  market_id?: string | null;
  market_probability?: number | null;
  scanned_at?: string | null;
}
export function predictFeedState(row: FeedRowLike): ResultState {
  // Only HARD-broken routes return broken_route at the feed layer.
  // No URL or no market_id means we cannot deep-link safely.
  if (!row.market_url || row.market_url.length < 8) return "broken_route";
  if (!row.market_id) return "broken_route";
  // REMASTER: rows with no resolved price are NOT broken — they're just
  // un-priced snapshots. Treating them as broken_route was the #1 source of
  // false "ROUTE BROKEN" experiences when clicking valid Kalshi rows.
  if (row.market_probability == null) return "stale_snapshot";
  if (row.scanned_at) {
    const ts = Date.parse(row.scanned_at);
    if (Number.isFinite(ts) && Date.now() - ts > 30 * 60 * 1000) return "stale_snapshot";
  }
  return "live_tradable";
}
